<script setup>
import BreadCrumbs from '@/components/breadcrumbs/BreadCrumbs.vue'
import ContentSubheaderPage from '@/components/content/ContentSubheaderPage.vue'
import SearchField from '@/components/fields/SearchField.vue'
import CategoryFilters from '@/components/filters/CategoryFilters.vue'
import LibraryBookCard from '@/components/library/LibraryBookCard.vue'
import { useLibraryStore } from '@/store/library'
import { storeToRefs } from 'pinia'
import { onBeforeUpdate, onMounted, ref, watch } from 'vue'
import * as helper from '@/store/helpers'
import debounce from 'lodash.debounce'

const libraryStore = useLibraryStore()
const { formatFilters } = storeToRefs(libraryStore)

const isMounted = ref(false)

const categories = ref([])
const authors = ref([])
const authorsSearchStr = ref('')

const searchStr = ref('')
const selectedCategories = ref([])
const selectedAuthors = ref([])
const isNewBooksSorting = ref(true)

const booksCount = ref(0)
const books = ref([])

const pageNumber = ref(1) // Текущая страница
const paginationCount = ref(15) // Количество новостей на 1 странице
const maxPage = ref(0)
const pageElems = ref([]) // ref-ссылки на dom-элементы (номера страниц)

const getMaxPage = (booksCount) => {
    return Math.ceil(booksCount / paginationCount.value)
}

const changePage = (page) => {
    pageNumber.value = page

    for (let i = 0; i < pageElems.value.length; i++) {
        if (pageElems.value[i].classList.contains('toggle')) {
            pageElems.value[i].classList.remove('toggle')
            break
        }
    }

    if (pageElems.value.length > 1) {
        pageElems.value[page - 1].classList.add('toggle')
    }

    document
        .getElementById('library-page')
        .scrollIntoView({ behavior: 'smooth' }) // Прокрутка страницы
}

const getBooks = async (
    isEmpty = true,
    searchString = '',
    categoriesId = [],
    authorsId = [],
    isDesc = true
) => {
    if (isEmpty) {
        booksCount.value = await helper.api('GET', '/library/count', {})
        await libraryStore.getBooks(0, paginationCount.value * pageNumber.value)
        books.value = libraryStore.books
    } else {
        booksCount.value = await helper.api('GET', '/library/count', {
            search_string: searchString,
            categories_id: categoriesId,
            authors_id: authorsId
        })
        await libraryStore.getBooks(
            paginationCount.value * (pageNumber.value - 1),
            paginationCount.value * pageNumber.value,
            searchString,
            categoriesId,
            authorsId,
            isDesc
        )
        books.value = libraryStore.books
    }
}

const changeSorting = () => {
    isNewBooksSorting.value = !isNewBooksSorting.value

    if (isNewBooksSorting.value) {
        document.getElementById('library-sort-arrow').style.transform =
            'rotate(0)'
        document.getElementById('library-sort-arrow-mobile').style.transform =
            'rotate(0)'
    } else {
        document.getElementById('library-sort-arrow').style.transform =
            'rotate(180deg)'
        document.getElementById('library-sort-arrow-mobile').style.transform =
            'rotate(180deg)'
    }
}

const clearFilters = () => {
    searchStr.value = ''
    selectedAuthors.value = []
    selectedCategories.value = []
}

watch(
    [searchStr, selectedCategories, selectedAuthors, isNewBooksSorting],
    debounce(async () => {
        changePage(1)
        await getBooks(
            false,
            searchStr.value,
            selectedCategories.value,
            selectedAuthors.value,
            isNewBooksSorting.value
        )
        maxPage.value = getMaxPage(booksCount.value)
    }, 500)
)

watch(
    authorsSearchStr,
    debounce(async () => {
        await libraryStore.getAuthors(authorsSearchStr.value)
        authors.value = formatFilters.value(true)
    }, 500)
)

watch(pageNumber, async () => {
    await getBooks(
        false,
        searchStr.value,
        selectedCategories.value,
        selectedAuthors.value,
        isNewBooksSorting.value
    )
})

onMounted(async () => {
    await libraryStore.getCategories()
    await libraryStore.getAuthors()
    categories.value = formatFilters.value(false)
    authors.value = formatFilters.value(true)

    await getBooks()
    maxPage.value = getMaxPage(booksCount.value)

    isMounted.value = true
})

onBeforeUpdate(() => {
    pageElems.value = []
})
</script>

<template>
    <div
        id="library-page"
        class="container"
    >
        <bread-crumbs />
        <content-subheader-page
            class="library-content-subheader"
            text="БИБЛИОТЕКА"
        />
        <v-container
            :fluid="true"
            class="pa-0 library-container"
        >
            <v-row class="ml-n2 mr-n2">
                <v-col
                    class="pl-2 pr-2 d-none d-lg-block"
                    cols="3"
                >
                    <div class="filter-card">
                        <h2
                            v-if="categories.length > 0"
                            class="filter-card-title"
                        >
                            Категория
                        </h2>
                        <category-filters
                            v-if="isMounted"
                            class="category-category-filters"
                            :conference-filters="categories"
                            :is-single="true"
                            @detect-select="
                                (selectedCategoriesArg) => {
                                    selectedCategories = selectedCategoriesArg
                                }
                            "
                        />
                        <h2 class="filter-card-title">Авторы</h2>
                        <v-text-field
                            v-model="authorsSearchStr"
                            class="author-search-field"
                            variant="outlined"
                            density="compact"
                            placeholder="Найти автора"
                            hide-details
                        />
                        <category-filters
                            v-if="isMounted"
                            class="author-category-filters"
                            :conference-filters="authors"
                            :is-single="true"
                            @detect-select="
                                (selectedAuthorsArg) => {
                                    selectedAuthors = selectedAuthorsArg
                                }
                            "
                        />
                    </div>
                </v-col>
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    lg="9"
                >
                    <div class="sorting-field mb-5 d-flex d-lg-none">
                        Сортировка:&nbsp;
                        <span @click="changeSorting">
                            по дате добавления
                            <img
                                id="library-sort-arrow-mobile"
                                src="/svg/dropdown_arrow.svg"
                                alt="arrow"
                            />
                        </span>
                    </div>
                    <search-field v-model="searchStr" />
                    <v-select
                        v-model="selectedCategories"
                        :items="categories"
                        class="d-lg-none"
                        label="Категории"
                        variant="outlined"
                        item-title="title"
                        item-value="id"
                        multiple
                    />
                    <v-autocomplete
                        v-model="selectedAuthors"
                        :items="authors"
                        class="d-lg-none"
                        label="Авторы"
                        variant="outlined"
                        item-title="title"
                        item-value="id"
                        no-data-text="Ничего не найдено"
                        multiple
                    />
                    <v-btn
                        class="text-none inputs-button d-lg-none"
                        height="60"
                        variant="flat"
                        color="#3B66D3"
                        @click="clearFilters"
                    >
                        Сбросить фильтры
                    </v-btn>
                    <div class="sorting-field d-none d-lg-flex">
                        Сортировка:&nbsp;
                        <span @click="changeSorting">
                            по дате добавления
                            <img
                                id="library-sort-arrow"
                                src="/svg/dropdown_arrow.svg"
                                alt="arrow"
                            />
                        </span>
                    </div>
                    <v-row class="ml-n2 mr-n2 mt-7">
                        <v-col
                            v-for="book in books"
                            :key="book.id"
                            class="pl-2 pr-2 pb-2 pt-2"
                            cols="12"
                            sm="6"
                            md="4"
                        >
                            <LibraryBookCard :bookInfo="book" />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-container>
        <v-row class="ml-n2 mr-n2">
            <v-col
                class="pl-2 pr-2 ml-auto"
                cols="12"
                lg="9"
            >
                <v-container
                    :fluid="true"
                    class="pa-0 pagination"
                    :class="{ 'd-none': maxPage < 2 }"
                >
                    <div
                        class="page-arrows"
                        @click="changePage(1)"
                    >
                        <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 1.75152L8.48329 0.5L0 7.5L8.48329 14.5L10 13.2485L3.03342 7.5L10 1.75152Z"
                                fill="#0076DE"
                            />
                            <path
                                d="M20 1.75152L18.4833 0.5L10 7.5L18.4833 14.5L20 13.2485L13.0334 7.5L20 1.75152Z"
                                fill="#0076DE"
                            />
                        </svg>
                    </div>
                    <div
                        v-for="(page, i) in maxPage"
                        v-show="
                            (pageNumber < 5
                                ? page <= 5
                                : page < pageNumber + 3) &&
                            (pageNumber < 5 ? true : page > pageNumber - 3)
                        "
                        :key="page"
                        :ref="
                            (el) => {
                                if (el) pageElems[i] = el
                            }
                        "
                        class="page"
                        :class="{ toggle: i === 0 }"
                        @click="changePage(page)"
                    >
                        {{ page }}
                    </div>
                    <div
                        class="page-arrows"
                        @click="changePage(maxPage)"
                    >
                        <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 13.2485L11.5167 14.5L20 7.5L11.5167 0.500001L10 1.75152L16.9666 7.5L10 13.2485Z"
                                fill="#0076DE"
                            />
                            <path
                                d="M0 13.2485L1.51671 14.5L10 7.5L1.51671 0.5L0 1.75152L6.96658 7.5L0 13.2485Z"
                                fill="#0076DE"
                            />
                        </svg>
                    </div>
                </v-container>
            </v-col>
        </v-row>
    </div>
</template>

<style lang="scss" scoped>
.container {
    margin-top: 32px;
    margin-left: 104px;
    margin-right: 104px;

    @media screen and (max-width: 767px) {
        margin-left: 40px;
        margin-right: 40px;
        margin-top: 24px;
    }
}

.library-content-subheader {
    text-align: left;
}

.library-container {
    margin-top: 28px;
}

.filter-card {
    max-height: 1030px;
    border-radius: 5px;
    background: #fff;
    box-shadow: 1px 1px 50px 10px #2632380d;
    padding: 0 25px 20px 25px;
    display: flex;
    flex-direction: column;
    align-items: start;
}

.filter-card-title {
    font-size: 28px;
    font-weight: 500;
    line-height: 36px;
    color: #0635ac;
    margin-top: 20px;
}

.author-search-field {
    width: 100%;
    margin-top: 20px;
}

.sorting-field {
    display: flex;
    justify-content: left;

    span {
        display: flex;
        align-items: center;
        color: #0635ac;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
            color: #144cd8;
        }
    }
}

.inputs-button {
    width: 100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
}

.category-category-filters {
  overflow-y: scroll;
  width: 100%;
}

.author-category-filters {
  overflow-y: scroll;
  margin-top: 10px;
}

.pagination {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: center;
    margin-top: 28px;

    .page-arrows {
        cursor: pointer;
    }

    .page {
        color: #0076de;
        font-size: 20px;
        line-height: 28px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .toggle {
        text-decoration: underline;
    }
}
</style>
